/**
 * bridgeQuestionnaire — pure logic that turns the dispatched fields-schema into
 * a clean, conditional patient questionnaire keyed by canonical machine_name.
 *
 * No React, no network — so it's unit-testable in isolation. The component
 * (BridgeQuestionnaire.jsx) renders the output; the bridge submit consumes the
 * {machine_name: value} answers.
 *
 * Design decisions baked in here:
 *  - Yes/No radio PAIRS (`*_yes` / `*_no`) collapse to ONE yes/no question.
 *  - Demographics already confirmed on the profile card (have a value, aren't
 *    insurance) are NOT re-asked — they pass straight to review.
 *  - Signature + signature-date fields are never questions (signed on the form;
 *    date is auto-set at signing).
 *  - Conditional fields (carried via `conditionalOn`) are hidden until their
 *    trigger answer matches — evaluated live against current answers, falling
 *    back to the trigger field's prefilled value (physician answer).
 */

import type { BridgeQuestion, SchemaField } from "../types/shared";
import { audienceMatcher } from "./signerAudience";

const DEMOGRAPHIC_HINTS = [
  "full_name", "first_name", "last_name", "date_of_birth", "email",
  "street_address", "address", "suite", "city", "province", "postal", "phone",
];

export function widgetForType(type: unknown) {
  switch (String(type || "").toLowerCase()) {
    case "checkbox": return "toggle";
    case "radio": return "choice";
    case "date": return "date";
    case "number": return "number";
    case "tel": return "tel";
    case "email": return "email";
    case "signature": return "signature";
    case "textarea":
    case "large_text": return "textarea";
    default: return "text";
  }
}

export function humanize(name: unknown) {
  return String(name || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

const lc = (s: unknown) => String(s || "").toLowerCase();
const isSig = (n: unknown) => lc(n).includes("signature");
const isSigDate = (n: unknown) => lc(n).includes("signature") && lc(n).includes("date");
const isDemographic = (n: unknown) => DEMOGRAPHIC_HINTS.some((h) => lc(n).includes(h)) && !lc(n).includes("insurance") && !lc(n).includes("dependent_cardholder");

const yesNoBase = (n: unknown) => {
  const m = lc(n).match(/^(.*)_(yes|no)$/);
  return m ? { base: m[1], side: m[2] } : null;
};

// The third member of a Yes / No / N-A radio joins its pair as `targets.na`
// instead of becoming a question of its own (mirrors the backend builder).
const thirdOptionBase = (n: unknown) => {
  const m = lc(n).match(/^(.*)_(na|n_a|not_applicable|unknown|unsure)$/);
  return m ? m[1] : null;
};

const truthy = (v: unknown) =>
  v !== undefined && v !== null && v !== "" && v !== false &&
  !["false", "no", "off", "0", "unchecked"].includes(lc(v));

/** Live answers keyed by machine_name|base. */
type Answers = Record<string, unknown>;

/** A field carried straight to review (not re-asked). */
interface PrefilledEntry {
  target: string;
  value: unknown;
  label: string;
}

interface BuildQuestionnaireOpts {
  answers?: Answers;
  skipFilled?: boolean;
  /** One audience, or several — a single-signer session owns both `patient`
   *  and `plan_holder`. See lib/signerAudience. */
  audience?: string | string[] | null;
  context?: Record<string, unknown>;
  [key: string]: unknown;
}

interface QuestionnaireResult {
  questions: BridgeQuestion[];
  hidden: BridgeQuestion[];
  prefilled: PrefilledEntry[];
}

/**
 * @param {Array<{name,label,type,value,conditionalOn?}>} fields  patient-fillable schema fields
 * @param {object} opts
 * @param {object} [opts.answers]  live answers {machine_name|base: value}
 * @param {boolean} [opts.skipFilledDemographics=true]
 * @returns {{ questions: Array, hidden: Array, prefilled: Array }}
 */
// Fields the patient already provides on the Confirm Details card. These are
// NEVER re-asked in the questionnaire — even when empty — because the card owns
// them; re-asking is the duplication the patient flow was showing. Their value
// still flows to review via `prefilled`.
//
// Scope is deliberately narrow: the suite (which the card collects in the
// address block) plus the card-owned *primary* insurance fields (policy/group #
// and member ID). General demographics (name, city, phone…) are left to the
// existing skip-when-filled rule. The *other-plan* fields
// (insurance_carrier_name, insurance_other_plan_policy_number,
// insurance_dependent_cardholder_birth_date) and the public-program fields are
// NOT card-owned and stay gated by their conditionals.
const CONFIRM_CARD_FIELDS = new Set([
  "patient_suite",
  "insurance_policy_group_number",
  "insurance_member_id",
]);

export function buildQuestionnaire(fields: SchemaField[], opts: BuildQuestionnaireOpts = {}): QuestionnaireResult {
  const answers = opts.answers || {};
  // Ask only what's empty: any field already on file (demographic OR insurance)
  // is shown on the review step, not re-asked. Set false to confirm everything.
  const skipFilled = opts.skipFilled !== false;

  // Audience filter: when set, drop questions owned by another audience.
  // Untagged fields always pass (back-compat with forms that predate the
  // question-model). The signer only sees their own asks — but on a
  // single-signer send "their own" is BOTH `patient` and `plan_holder`, because
  // the patient is also the plan member (lib/signerAudience). Membership, not
  // equality.
  const matchesAudience = audienceMatcher(opts.audience);
  const roleFields = matchesAudience
    ? fields.filter((f) => matchesAudience(f.audience))
    : fields;

  // 1) Collapse yes/no pairs into one logical question keyed by the base name.
  const pairs = new Map<string, { yes?: SchemaField; no?: SchemaField; na?: SchemaField }>(); // base -> { yes, no, na? }
  for (const f of roleFields) {
    const yn = yesNoBase(f.name);
    if (yn) {
      const e: { yes?: SchemaField; no?: SchemaField; na?: SchemaField } = pairs.get(yn.base) || {};
      (e as Record<string, SchemaField>)[yn.side] = f;
      pairs.set(yn.base, e);
    }
  }
  for (const f of roleFields) {
    const tb = thirdOptionBase(f.name);
    if (tb && pairs.has(tb)) pairs.get(tb)!.na = f;
  }

  const made = new Set<string>();
  const questions: BridgeQuestion[] = [];
  const prefilled: PrefilledEntry[] = [];

  const pushQuestion = (q: BridgeQuestion) => {
    if (made.has(q.target as string)) return;
    made.add(q.target as string);
    questions.push(q);
  };

  // Prefer the durable model's plain-language patient_stem, then the faithful
  // stem, then the field label, then a humanized name.
  const stemOf = (f: SchemaField) => (f && ((f as Record<string, unknown>).patient_stem || (f as Record<string, unknown>).stem || f.label)) || humanize(f && f.name);

  for (const f of roleFields) {
    // Signature + signature-date: never a question — by name AND by type (a
    // signature widget with an odd machine_name must still never be asked).
    if (isSig(f.name) || lc((f as Record<string, unknown>).type) === "signature") continue;
    // Confirm-card-owned field: pass its value to review, never ask it here.
    // Case-insensitive: schema names vary (patient_suite vs Patient_Suite).
    if (CONFIRM_CARD_FIELDS.has(lc(f.name))) {
      prefilled.push({ target: f.name, value: f.value, label: f.label || humanize(f.name) });
      continue;
    }
    // Third member of a Yes/No pair: carried on the pair question, never asked alone.
    const tb = thirdOptionBase(f.name);
    if (tb && pairs.has(tb)) continue;

    const yn = yesNoBase(f.name);

    if (yn) {
      if (made.has(yn.base)) continue;
      const pair = pairs.get(yn.base)!;
      const rep = pair.yes || pair.no; // representative field for label/conditional
      // label: strip a trailing "Yes"/"No" from the field label
      const label = humanize(yn.base).replace(/\s+(Yes|No)$/i, "");
      // pre-answer from whichever side is currently checked
      const ynValue = pair.yes && truthy(pair.yes.value) ? "yes"
        : (pair.no && truthy(pair.no.value) ? "no"
        : (pair.na && truthy(pair.na.value) ? "na" : ""));
      if (skipFilled && ynValue) {
        made.add(yn.base);
        prefilled.push({ target: yn.base, value: ynValue, label });
        continue;
      }
      pushQuestionTo(questions, made, {
        target: yn.base,
        targets: {
          yes: pair.yes ? pair.yes.name : null,
          no: pair.no ? pair.no.name : null,
          ...(pair.na ? { na: pair.na.name } : {}),
        },
        label: (rep && ((rep as Record<string, unknown>).patient_stem || (rep as Record<string, unknown>).stem))
          || (rep && rep.label ? rep.label.replace(/\s+(Yes|No)$/i, "") : label),
        widget: "yesno",
        required: !!(rep && rep.required),
        section: (rep && rep.section)
          || (lc(yn.base).includes("insurance") || lc(yn.base).includes("medicare") || lc(yn.base).includes("publicfunded") ? "insurance" : "other"),
        conditionalOn: rep ? rep.conditionalOn || null : null,
        value: ynValue,
      } as BridgeQuestion);
      continue;
    }

    const filled = f.value !== undefined && f.value !== null && String(f.value).trim() !== "";
    if (skipFilled && filled) {
      prefilled.push({ target: f.name, value: f.value, label: f.label || humanize(f.name) });
      continue;
    }

    pushQuestionTo(questions, made, {
      target: f.name,
      label: stemOf(f),
      widget: widgetForType(f.type),
      required: !!f.required,
      section: f.section
        || (lc(f.name).includes("insurance") ? "insurance" : (isDemographic(f.name) ? "demographics" : "other")),
      conditionalOn: f.conditionalOn || null,
      value: filled ? f.value : "",
    } as BridgeQuestion);
  }

  // 2) Evaluate conditional visibility.
  const { visible, hidden } = partitionVisible(questions, {
    answers,
    context: opts.context || {},
  });

  return { questions: visible, hidden, prefilled };
}

/**
 * Split a question list into visible/hidden by evaluating each question's
 * `conditionalOn` {trigger, show_when} against the current answers. This is the
 * one genuinely interactive piece that must run client-side as the patient
 * answers — it is shared by the legacy client builder (buildQuestionnaire) and
 * the new backend-owned path (renderBackendQuestions). Mirrors
 * questionnaire_builder.partition_visible() on the backend.
 *
 * @param {Array} questions  the full (ordered) question list
 * @param {object} opts {answers, context}
 * @returns {{visible: Array, hidden: Array}}
 */
export function partitionVisible(
  questions: BridgeQuestion[],
  opts: { answers?: Answers; context?: Record<string, unknown> } = {}
): { visible: BridgeQuestion[]; hidden: BridgeQuestion[] } {
  const list = questions || [];
  const answers = opts.answers || {};
  const context = opts.context || {};
  const answerFor = (key: string) => {
    if (answers[key] !== undefined) return answers[key];
    if (context[key] !== undefined) return context[key];
    const q = list.find((x) => x.target === key);
    return q ? q.value : undefined;
  };
  const visible: BridgeQuestion[] = [];
  const hidden: BridgeQuestion[] = [];
  for (const q of list) {
    if (q.conditionalOn && q.conditionalOn.trigger) {
      const want = lc(q.conditionalOn.show_when || "yes");
      const raw = answerFor(q.conditionalOn.trigger);
      let shown: boolean;
      if (want !== "yes" && want !== "no") {
        // `show_when` names one option of a choice ("show when Other is
        // picked"): the answer is that option's target, or a list of them for
        // a pick-several. Mirrors questionnaire_builder.partition_visible.
        const picked = Array.isArray(raw) ? raw : [raw];
        shown = picked.some((p) => lc(p) === want);
      } else {
        const have = lc(raw);
        // "na" is a Yes/No question's third answer, not a yes: picking Not
        // applicable must not open the follow-up written for a Yes.
        shown = want === "yes" ? have === "yes" || (have !== "na" && truthy(have)) : have === "no";
      }
      if (!shown) { hidden.push(q); continue; }
    }
    visible.push(q);
  }
  return { visible, hidden };
}

/**
 * BACKEND-OWNED PATH: render the question list the backend already built
 * (sealed into the dispatch payload as `questions`). The backend owns ALL the
 * structural decisions — yes/no collapse, audience filter, card-owned skip,
 * plain-language stems, sections, conditionalOn — so this function does NOT
 * re-derive any of that. It only applies the two runtime concerns that need
 * in-session data the backend can't have at dispatch time:
 *   (1) suppress a plain (non-yesno) question whose value the patient has
 *       already provided this session (e.g. demographics confirmed on the
 *       Confirm Details card and seeded into `fields`), passing it to review; and
 *   (2) live conditional visibility (partitionVisible).
 *
 * @param {Array} allQuestions  the backend-built questions[] (full, ordered)
 * @param {object} opts {fields, answers, context}
 *   fields  the schema fields (carry seeded/confirmed `value`s) for skip-filled
 * @returns {{questions: Array, hidden: Array, prefilled: Array}}
 */
export function renderBackendQuestions(
  allQuestions: BridgeQuestion[],
  opts: {
    fields?: SchemaField[];
    answers?: Answers;
    context?: Record<string, unknown>;
    /** Keep a plain question whose box already holds a value IN the question
     *  list, carrying that value, instead of setting it aside as `prefilled`.
     *  The two-part questionnaire ("here's what we have — confirm or edit",
     *  then what we don't) needs the answered ones; without this the plan
     *  holder's link showed only the one choice question that had no box of
     *  its own, and none of the eleven known facts (Michael, 2026-10-08). */
    keepAnswered?: boolean;
  } = {}
): QuestionnaireResult {
  const all = allQuestions || [];
  const fields = opts.fields || [];
  const answers = opts.answers || {};
  const context = opts.context || {};
  const keepAnswered = !!opts.keepAnswered;

  const valByName: Record<string, unknown> = {};
  const firstPage = new Set<string>();
  for (const f of fields) {
    if (f && f.name) valByName[lc(f.name)] = f.value;
    if (f && f.name && f.from_first_page) firstPage.add(lc(f.name));
  }

  const asked: BridgeQuestion[] = [];
  const prefilled: PrefilledEntry[] = [];
  for (const q of all) {
    // The backend already moved any dispatch-time-answered Yes/No to
    // prefilled. One is seeded this session: the first page's "no provincial
    // coverage" ticks a provincial claim's Not applicable (imp_5559e114). Only
    // a box the seeding marked `from_first_page` answers one — a name-based
    // demographic seed in a Yes box ("has_email_yes") must not.
    if (q.widget === "yesno" && !truthy(q.value) && q.targets) {
      const side = (["yes", "no", "na"] as const).find((s) => {
        const t = q.targets?.[s];
        return !!t && firstPage.has(lc(t)) && truthy(valByName[lc(t)]);
      });
      if (side) {
        if (keepAnswered) asked.push({ ...q, value: side });
        else prefilled.push({ target: q.target as string, value: side, label: q.label as string });
        continue;
      }
    }
    // Plain fields whose value the patient already provided this session are
    // not re-asked — unless the caller keeps them, to confirm.
    if (q.widget !== "yesno") {
      const v = valByName[lc(q.target)];
      if (v !== undefined && v !== null && String(v).trim() !== "") {
        if (keepAnswered) {
          asked.push({ ...q, value: v });
        } else {
          prefilled.push({ target: q.target as string, value: v, label: q.label as string });
        }
        continue;
      }
    }
    asked.push(q);
  }

  const { visible, hidden } = partitionVisible(asked, { answers, context });
  return { questions: visible, hidden, prefilled };
}

// What a chosen option writes into its box: a check mark, the same glyph the
// Mac's fill reads for a ticked checkbox / radio member.
export const CHECK = "✓";

/** Blank every box a question owns — used when a conditional question is
 *  hidden again, so an answer given before the trigger flipped never ships. */
export function clearQuestionTargets(q: BridgeQuestion, out: Record<string, unknown>): void {
  if (q.widget === "yesno") {
    if (q.targets?.yes) out[q.targets.yes] = "";
    if (q.targets?.no) out[q.targets.no] = "";
    if (q.targets?.na) out[q.targets.na] = "";
  } else if (q.widget === "choice" || q.widget === "multi") {
    for (const opt of q.options || []) out[opt.target] = "";
  } else {
    out[q.target as string] = "";
  }
}

/**
 * The questionnaire's answers as the bridge submission carries them,
 * `{ machine_name: value }`. Answers are keyed by question target in the
 * widget's own vocabulary ("yes"/"no"/"na", an option's target, an array of
 * targets for multi, "yes" for a toggle, text otherwise); a question with no
 * entry in `answers` is left out so the canonical keeps its value. Every
 * member of a choice is written, ticked or blank, so a changed answer clears
 * the old tick. Shared by BridgeQuestionnaire and the plan holder's journey
 * so both legs reach the Mac in one shape.
 */
export function emitQuestionnaireAnswers(
  questions: BridgeQuestion[],
  hidden: BridgeQuestion[],
  answers: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const q of questions) {
    const v = answers[q.target as string];
    if (v === undefined) continue;
    if (q.widget === "yesno") {
      if (q.targets?.yes) out[q.targets.yes] = v === "yes" ? CHECK : "";
      if (q.targets?.no) out[q.targets.no] = v === "no" ? CHECK : "";
      if (q.targets?.na) out[q.targets.na] = v === "na" ? CHECK : "";
    } else if (q.widget === "choice") {
      for (const opt of q.options || []) out[opt.target] = v === opt.target ? CHECK : "";
    } else if (q.widget === "multi") {
      const arr = Array.isArray(v) ? v : [];
      for (const opt of q.options || []) out[opt.target] = arr.includes(opt.target) ? CHECK : "";
    } else if (q.widget === "toggle") {
      out[q.target as string] = v === "yes" ? CHECK : "";
    } else {
      out[q.target as string] = v;
    }
  }
  for (const q of hidden) clearQuestionTargets(q, out);
  return out;
}

function pushQuestionTo(arr: BridgeQuestion[], made: Set<string>, q: BridgeQuestion) {
  if (made.has(q.target as string)) return;
  made.add(q.target as string);
  arr.push(q);
}

export default buildQuestionnaire;
